import { useCheckoutFlow } from "@/contexts/checkout-flow/checkout-flow.provider";
import { formatCurrency } from "@/utils/currency.util";
import { Component, For, Show, createSignal, createMemo } from "solid-js";
import {
  ReviewSectionDivider,
  ReviewSectionWrapper,
} from "../review-section/ReviewSection";
import { ConfirmCancelModal } from "../modals/cancel-modal/ConfirmCancelModal";
import {
  isPastCancellationDeadline,
  isPreviousMonth,
} from "@/utils/checkout.utils";
import { publishCancelCampaignCancelClick } from "@/services/analytics.service";

interface ReviewProps {
  showCancelButton?: boolean;
}

const CampaignDetailsSection: Component<{ isPreviousCampaign: boolean }> = (
  props,
) => {
  const { selections } = useCheckoutFlow();

  return (
    <ReviewSectionWrapper title="Campaign Details">
      <div class="grid grid-cols-2 gap-y-FsSpacing2x">
        <For each={selections()}>
          {(item) => (
            <>
              <span>{`${item.month} ${item.year}`}</span>
              <span class="text-right" data-testid="campaign-price">
                <Show when={!props.isPreviousCampaign}>
                  {formatCurrency(item.price)}
                </Show>
              </span>
            </>
          )}
        </For>
      </div>
    </ReviewSectionWrapper>
  );
};

const PaymentSummarySection: Component<{ isPreviousCampaign: boolean }> = (
  props,
) => {
  const { totalPrice } = useCheckoutFlow();

  return (
    <ReviewSectionWrapper title="Payment Summary">
      <div class="flex justify-between items-center gap-FsSpacing2x mb-FsSpacing2x">
        <span>Total</span>
        <span data-testid="payment-total">
          <Show
            when={!props.isPreviousCampaign}
            fallback="For pricing on this campaign, refer to your invoice."
          >
            {formatCurrency(totalPrice())}
          </Show>
        </span>
      </div>
      <fd-mfe-alert prop:variant="informational" prop:leadingIcon={true}>
        All applicable fees and taxes for the Featured Dealer campaigns will be
        added to your monthly subscription invoice (based on the month in which
        the campaign will run). Payment due date will be dictated by the
        invoice.
      </fd-mfe-alert>
    </ReviewSectionWrapper>
  );
};

const CancelButton = (props: {
  onOpenModal: () => void;
  isPastDeadline: boolean;
}) => {
  return (
    <Show
      when={!props.isPastDeadline}
      fallback={
        <fd-mfe-tooltip
          prop:tooltipContent="Active campaigns can't be canceled"
          prop:position="bottom"
          data-testid="cancel-restriction-tooltip"
        >
          <fd-mfe-button
            prop:variant="critical"
            prop:rank="secondary"
            prop:disabled={true}
            class="pt-FsSpacing5x"
            data-testid="cancel-reservation-button"
          >
            Cancel reservation
          </fd-mfe-button>
        </fd-mfe-tooltip>
      }
    >
      <fd-mfe-button
        prop:variant="critical"
        prop:rank="secondary"
        prop:disabled={props.isPastDeadline}
        class="pt-FsSpacing5x"
        onClick={props.onOpenModal}
        data-testid="cancel-reservation-button"
      >
        Cancel reservation
      </fd-mfe-button>
    </Show>
  );
};

const CancellationsInfoSection: Component<{
  onOpenModal: () => void;
  showCancelButton?: boolean;
  isPastDeadline: boolean;
}> = (props) => {
  return (
    <ReviewSectionWrapper title="Cancellations & Modifications">
      <p class="mb-FsSpacing2x">
        You may cancel or modify your reservation(s) up to 15 days before the
        start of each reservation.
      </p>
      <p class="mb-FsSpacing2x">
        Requested campaigns reflected as "scheduled" are still subject to
        eligibility prior to campaign start date.
      </p>
      <Show when={props.showCancelButton}>
        <CancelButton
          onOpenModal={props.onOpenModal}
          isPastDeadline={props.isPastDeadline}
        />
      </Show>
    </ReviewSectionWrapper>
  );
};

const Review: Component<ReviewProps> = (props) => {
  const { cancelCampaign } = useCheckoutFlow();
  const [showConfirmCancelModal, setShowConfirmCancelModal] =
    createSignal(false);

  const isPastDeadline = createMemo(() => {
    const campaign = cancelCampaign();
    if (!campaign) return false;
    return isPastCancellationDeadline(campaign.month, campaign.year);
  });

  const isPreviousCampaign = createMemo(() => {
    const campaign = cancelCampaign();
    if (!campaign) return false;
    return isPreviousMonth(campaign.month, campaign.year);
  });

  return (
    <>
      <div class="@xs:border @xs:border-solid @xs:border-FsClrBorderNeutEmphLow @xs:rounded-FsCornerRadiusMedium @xs:p-FsSpacing4x">
        <CampaignDetailsSection isPreviousCampaign={isPreviousCampaign()} />
        <ReviewSectionDivider />
        <PaymentSummarySection isPreviousCampaign={isPreviousCampaign()} />
        <ReviewSectionDivider />
        <CancellationsInfoSection
          onOpenModal={() => {
            publishCancelCampaignCancelClick();
            setShowConfirmCancelModal(true);
          }}
          showCancelButton={props.showCancelButton}
          isPastDeadline={isPastDeadline()}
        />
      </div>
      <Show when={props.showCancelButton}>
        <ConfirmCancelModal
          isOpen={showConfirmCancelModal()}
          onClose={() => setShowConfirmCancelModal(false)}
        />
      </Show>
    </>
  );
};

export default Review;
